/**
 * The comment layer — §8.1's margin and its all-comments panel, over one list.
 *
 * ## Two surfaces, one query
 *
 * `GET /api/comments` returns every thread this caller may see, each carrying
 * `inMargin`. The margin is the threads with that flag; the panel is all of
 * them. Serving both from one response is what makes §8.1's central promise a
 * property of the data rather than a coincidence between two fetches: **when a
 * comment's anchor is edited away it leaves the margin and stays in the panel**,
 * because it is the same row with one field flipped. A UI that hid orphaned
 * threads would fail the acceptance criterion no matter what the database kept —
 * so nothing here filters by state, and the panel's count is the whole list.
 *
 * ## What this component decides, and what it refuses to
 *
 * It decides layout, selection, and when to ask again. It does **not** decide
 * whether a comment is orphaned (the API does, against the stored body, and
 * `anchor.ts` says why a second opinion in the browser would be worse than no
 * opinion), whether this person may apply a suggestion (`document.edit`,
 * resolved in the transaction that writes), or whether they may see an internal
 * comment (the list simply does not contain ones they may not).
 *
 * ## The two seconds nobody sees
 *
 * Anchors resolve against the *stored* draft. Yjs holds keystrokes until
 * `apps/collab` snapshots them on debounce (§8.3), so between deleting a
 * sentence and the snapshot landing, the server still believes the comment on it
 * is anchored. Two mechanisms cover that gap and neither pretends to be the
 * other: the decorations map through every transaction, so the highlight
 * disappears with the text immediately; and {@link SETTLE_MS} after typing
 * stops, the list is asked again, which is when the label catches up.
 */

import { useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/core';
import { type ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ToggleButton, ToggleButtonGroup } from 'react-aria-components';
import { useDocument, useMe, usePeoplePicker, useSpace } from '../../api/queries.ts';
import { roleCarries } from '../../components/capabilities.ts';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { cn } from '../../components/ui/cn.ts';
import { Alert, ErrorState, Loading } from '../../components/ui/feedback.tsx';
import { anchorFromSelection, type BlockAnchor, blockPreview, locateAnchor } from './anchor.ts';
import type { AudienceContext } from './audience.ts';
import { CommentComposer } from './composer.tsx';
import {
  type CommentHighlight,
  commentHighlightKey,
  commentHighlightPlugin,
  scrollToThread,
  setHighlights,
} from './highlights.ts';
import { commentKeys, useComments } from './queries.ts';
import { CommentStyles } from './styles.tsx';
import { CommentThreadCard } from './thread.tsx';
import type { ApplySuggestionResponse, CommentThreadView } from './types.ts';

/**
 * How long after the last keystroke the comment list is asked again.
 *
 * Hocuspocus debounces `onStoreDocument` at two seconds, and that write is what
 * moves `document.content` and runs the orphan sweep. Asking sooner reads the
 * body from before the edit and re-renders the same answer; this is the first
 * moment the question has a new one.
 */
const SETTLE_MS = 3_500;

export interface CommentMarginProps {
  /** The live editor. Decorations are registered on it; its schema is untouched. */
  editor: Editor;
  nodeId: string;
  /** §8.2's Apply, and §8.1's rule that closing someone else's thread is editorial. */
  canEdit: boolean;
  /** Edits Yjs has not acknowledged. A comment on them may be refused (§8.3). */
  pendingSync: boolean;
}

type Scope = 'margin' | 'all';

export function CommentMargin({
  editor,
  nodeId,
  canEdit,
  pendingSync,
}: CommentMarginProps): ReactElement {
  const queryClient = useQueryClient();
  const me = useMe();
  const user = me.data?.user;
  const comments = useComments(nodeId);

  const [scope, setScope] = useState<Scope>('margin');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [composing, setComposing] = useState<{ anchor: BlockAnchor; preview: string } | null>(null);
  const [selectionKind, setSelectionKind] = useState<'none' | 'block' | 'range'>('none');
  const [applyNotice, setApplyNotice] = useState<string | null>(null);

  const threads = useMemo(() => comments.data?.threads ?? [], [comments.data]);
  const inMargin = useMemo(() => threads.filter((thread) => thread.comment.inMargin), [threads]);
  const orphanCount = threads.filter((thread) => thread.comment.state === 'orphaned').length;

  // The document and its space are already in the cache on the page that mounts
  // the editor; they are read again here rather than threaded through the
  // editor's prop contract, which §5.1's component is entitled to keep to itself.
  const document = useDocument(nodeId);
  const space = useSpace(document.data?.document.spaceId ?? '');
  const picker = usePeoplePicker(threads.length > 0);

  const names = useMemo(() => {
    const map = new Map<string, string>();
    for (const entry of picker.data?.people ?? []) map.set(entry.id, entry.name);
    return map;
  }, [picker.data]);

  const nameOf = useCallback(
    // §2.6 keeps deactivated people out of pickers while historical records go on
    // rendering them, and a comment is a historical record — so an id the picker
    // cannot resolve gets a person-shaped word rather than a uuid.
    (personId: string) => names.get(personId) ?? 'Someone',
    [names],
  );

  const audience: AudienceContext = {
    // The same question `apps/api/src/comments/visibility.ts` asks — does this
    // role's baseline carry `document.edit` at all — through the mirror
    // `components/capabilities.ts` exists to provide. It explains a control; the
    // API decides what is returned and the database forces a client's comment
    // external regardless.
    seesInternal: user ? roleCarries(user.role, 'document.edit') : false,
    spaceKind: space.data?.space.kind ?? null,
    spaceName: space.data?.space.name ?? null,
  };

  // ---------------------------------------------------------------------------
  // The editor side
  // ---------------------------------------------------------------------------

  // Held in a ref, and refreshed after every render, so the plugin can be
  // registered once per editor while the handler still sees this render's
  // threads. A handler captured at registration would keep answering with the
  // margin as it stood when the editor was built.
  const selectRef = useRef<(commentId: string) => void>(() => {});
  useEffect(() => {
    selectRef.current = (commentId) => {
      setSelectedId(commentId);
      // A click in the text is a request for that thread, and it may be one the
      // margin does not list — a suggestion resolved a moment ago still has its
      // decoration mapped through. Widening the scope is cheaper than a
      // selection that highlights nothing anybody can find.
      if (!inMargin.some((thread) => thread.comment.id === commentId)) setScope('all');
    };
  });

  useEffect(() => {
    const plugin = commentHighlightPlugin({ onSelect: (id) => selectRef.current(id) });
    editor.registerPlugin(plugin);
    return () => {
      if (!editor.isDestroyed) editor.unregisterPlugin(commentHighlightKey);
    };
  }, [editor]);

  const highlights: CommentHighlight[] = useMemo(
    () =>
      threads
        .filter((thread) => thread.comment.inMargin && thread.comment.anchor !== null)
        .map((thread) => {
          const anchor = thread.comment.anchor as NonNullable<
            CommentThreadView['comment']['anchor']
          >;
          return {
            commentId: thread.comment.id,
            anchor: {
              blockId: anchor.blockId,
              start: anchor.start ?? 0,
              end: anchor.end ?? 0,
              text: anchor.text,
            },
            internal: thread.comment.internal,
            suggestion: thread.comment.suggestedText !== null,
          };
        }),
    [threads],
  );

  useEffect(() => {
    setHighlights(editor, highlights, selectedId);
  }, [editor, highlights, selectedId]);

  useEffect(() => {
    const sync = () => {
      const anchor = anchorFromSelection(editor.state);
      // A primitive, so typing — which moves the selection on every keystroke —
      // re-renders this component only when the *kind* of thing selected
      // changes. The anchor itself is computed when the composer opens.
      setSelectionKind(anchor === null ? 'none' : anchor.text.length > 0 ? 'range' : 'block');
    };
    sync();
    editor.on('selectionUpdate', sync);
    return () => {
      editor.off('selectionUpdate', sync);
    };
  }, [editor]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const settle = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: commentKeys.all(nodeId) });
      }, SETTLE_MS);
    };
    // Fires for a co-author's edit as well as this person's, which is the case
    // that matters: somebody else deleting the clause is exactly when a comment
    // on it becomes a record of something that used to be there.
    editor.on('update', settle);
    return () => {
      editor.off('update', settle);
      if (timer) clearTimeout(timer);
    };
  }, [editor, nodeId, queryClient]);

  /**
   * §8.2's apply wrote the JSON column. This puts the same words into the live
   * `Y.Doc`.
   *
   * §8.3 makes Yjs the live state and the column a snapshot of it, so a REST
   * write during an open editing session is invisible here and would be
   * overwritten by the next debounce. Rather than add a second bridge — which
   * `apps/collab/src/snapshot.ts` is emphatically the only one of — the edit is
   * replayed through the editor, which is how any other edit reaches the
   * document: one client makes it, Yjs distributes it, the snapshot stores it.
   *
   * The text is found again rather than trusted at the offsets the server used,
   * because those index the stored draft and this document is up to two seconds
   * ahead of it. When it cannot be found, nothing is written and the notice says
   * so — putting a client agreement's words at an approximate position is the
   * failure §8.2's own resolver refuses, and it would be no better here.
   */
  const reconcileApplied = useCallback(
    (result: ApplySuggestionResponse) => {
      setSelectedId(result.comment.id);
      if (!canEdit) return;

      const { replaced } = result;
      const range = locateAnchor(editor.state.doc, {
        blockId: replaced.blockId,
        start: replaced.start,
        end: replaced.end,
        text: replaced.from,
      });
      if (!range || range.to <= range.from) {
        setApplyNotice(
          'The suggestion was applied to the saved draft, but the text it replaces has already ' +
            'moved in this editor. Reload before editing further so the two agree.',
        );
        return;
      }

      const { state } = editor.view;
      // Marks follow the text being replaced, as they do server-side: proposing
      // new wording for a bold phrase should not quietly unbold it.
      const marks = state.doc.nodeAt(range.from)?.marks ?? [];
      const transaction = state.tr;
      if (replaced.to === '') transaction.delete(range.from, range.to);
      else transaction.replaceWith(range.from, range.to, state.schema.text(replaced.to, marks));
      editor.view.dispatch(transaction);
      setApplyNotice(null);
    },
    [canEdit, editor],
  );

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  const startComposing = () => {
    const anchor = anchorFromSelection(editor.state);
    if (!anchor) return;
    setComposing({
      anchor,
      // Read once, here, and not stored: a block-level anchor quotes nothing on
      // purpose, so the composer needs the block's current words to name what is
      // about to be commented on.
      preview: anchor.text === '' ? blockPreview(editor.state.doc, anchor.blockId) : '',
    });
  };

  const shown = scope === 'margin' ? inMargin : threads;
  const viewerId = user?.id ?? '';

  return (
    <section aria-label="Comments" className="flex flex-col gap-3 border-t border-secondary pt-4">
      <CommentStyles />

      <header className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-primary">
          Comments
          {/* A count of the threads that have left the margin, and a way into
              the list that still holds them. §8.1 keeps an orphaned comment
              readable; a badge that only counted them would tell someone their
              objection had been swallowed and offer no way to check. */}
          {orphanCount > 0 ? (
            <Button
              size="sm"
              variant="tertiary"
              className="h-auto px-0 py-0 font-normal"
              onPress={() => setScope('all')}
            >
              <Badge tone="warning">{orphanCount} on removed content</Badge>
            </Button>
          ) : null}
        </h2>

        <div className="flex flex-wrap items-center gap-2">
          <ToggleButtonGroup
            aria-label="Which comments to show"
            selectionMode="single"
            disallowEmptySelection
            selectedKeys={[scope]}
            onSelectionChange={(keys) => {
              const [first] = [...keys];
              if (first === 'margin' || first === 'all') setScope(first);
            }}
            className="flex items-center gap-1"
          >
            <ScopeButton id="margin" label={`In the text (${inMargin.length})`} />
            <ScopeButton id="all" label={`All comments (${threads.length})`} />
          </ToggleButtonGroup>
          <Button
            size="sm"
            variant="secondary"
            // Never before the session resolves. The audience control is
            // derived from this person's role, and a composer that opened
            // without one would describe the wrong audience for the moment it
            // took `GET /auth/me` to answer — on the one control where being
            // wrong for a moment is the whole risk (§8.1).
            isDisabled={selectionKind === 'none' || composing !== null || !user}
            onPress={startComposing}
          >
            {selectionKind === 'range' ? 'Comment on selection' : 'Comment on this block'}
          </Button>
        </div>
      </header>

      {selectionKind === 'none' && composing === null ? (
        <p className="text-xs text-tertiary">
          Put the cursor in a block, or select the words a comment is about — an anchor is a block
          and a range inside it, which is what survives the rest of the document being rewritten.
        </p>
      ) : null}

      {composing && user ? (
        <CommentComposer
          nodeId={nodeId}
          anchor={composing.anchor}
          blockPreview={composing.preview}
          audience={audience}
          pendingSync={pendingSync}
          onCancel={() => setComposing(null)}
          onCreated={(commentId) => {
            setComposing(null);
            setSelectedId(commentId);
            setScope('margin');
          }}
        />
      ) : null}

      {applyNotice ? <Alert tone="warning">{applyNotice}</Alert> : null}

      {/* `isLoading` rather than `isPending`: a background refetch — which is
          what the settle timer causes on every burst of typing — must not
          replace the margin with a spinner, and a query that has never run
          stays pending forever. */}
      {comments.isLoading ? <Loading label="Loading comments…" /> : null}
      {comments.isError ? <ErrorState error={comments.error} /> : null}

      {!comments.isLoading && !comments.isError && shown.length === 0 ? (
        <EmptyScope scope={scope} total={threads.length} />
      ) : null}

      <ol className="flex flex-col gap-2">
        {shown.map((thread) => (
          <li key={thread.comment.id}>
            <CommentThreadCard
              thread={thread}
              nodeId={nodeId}
              viewerId={viewerId}
              canEdit={canEdit}
              isSelected={selectedId === thread.comment.id}
              onSelect={() => setSelectedId(thread.comment.id)}
              {...(thread.comment.inMargin
                ? { onShowInText: () => scrollToThread(editor, thread.comment.id) }
                : {})}
              nameOf={nameOf}
              onApplied={reconcileApplied}
            />
          </li>
        ))}
      </ol>
    </section>
  );
}

/**
 * One end of the filter. Inside a `ToggleButtonGroup`, so the pair is one
 * control for a keyboard and a screen reader rather than two buttons that
 * happen to disagree.
 */
function ScopeButton({ id, label }: { id: Scope; label: string }): ReactElement {
  return (
    <ToggleButton
      id={id}
      className={cn(
        'cursor-pointer rounded-md px-2.5 py-1 text-xs font-semibold transition-colors',
        'text-tertiary data-hovered:bg-primary-hover',
        'data-selected:bg-brand-primary data-selected:text-brand-secondary',
        'data-focus-visible:outline-2 data-focus-visible:outline-offset-2',
        'data-focus-visible:outline-brand-500',
      )}
    >
      {label}
    </ToggleButton>
  );
}

/**
 * The empty states, which are three different facts and not one.
 *
 * The middle one is §8.1's, and it is the reason this is a component rather than
 * a string: a document whose every comment has been orphaned shows an empty
 * margin, and without a sentence pointing at the panel that reads as "the
 * comments are gone" — which is precisely the impression the orphan rule exists
 * to prevent.
 */
function EmptyScope({ scope, total }: { scope: Scope; total: number }): ReactElement {
  if (total === 0) {
    return (
      <p className="px-1 py-4 text-sm text-tertiary">
        No comments yet. Select the words a comment is about and press Comment.
      </p>
    );
  }
  if (scope === 'margin') {
    return (
      <p className="px-1 py-4 text-sm text-tertiary">
        Nothing is anchored in the text right now. All {total} threads are still readable under All
        comments — a comment whose text was edited away is kept, labelled as referring to removed
        content.
      </p>
    );
  }
  return <p className="px-1 py-4 text-sm text-tertiary">No comments.</p>;
}
